import Config from 'react-native-config';
// const refreshTokenStorage = createStorage({key: 'auth-refresh-token'});
import * as Keychain from 'react-native-keychain';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios, {AxiosRequestConfig, AxiosResponse} from 'axios';
import {Method} from 'axios';
// @ts-expect-error TS(7016): Could not find a declaration file for module 'json... Remove this comment to see the full error message
import {snakeToCamelCase} from 'json-style-converter';
import {get, isEmpty} from 'lodash';
import {flow} from 'lodash/fp';
import {stringify} from 'query-string';

import {REQUEST_GET} from '/enum/request.enum';
import {getDateISOStringWithoutTimezoneWithMill} from '/util/date.util';
import {ApiError} from '/util/error-tracking/api-error';
import {isJSON} from '/util/json.util';
import {getPerfMonitorDefaultInstance} from '/util/perf-monitor/perf-monitor.util';
import {Logger} from '/util/splunk/logger.util';

import {redirectToLogin} from './auth.util';

export const API_TYPE = {
  ['CDP_API_URL']: Config.API_URL,
  ['MULE_API_URL']: Config.MULE_API_URL,
  ['MOCK_API_URL']: Config.MOCK_API_URL,
  ['HEALTHY_HABITS_ROOT']: Config.HEALTHY_HABITS_ROOT,
  ['HEALTH_PROFILE_ROOT']: Config.HEALTH_PROFILE_ROOT,
  ['HEALTH_JOURNEY_MULE_API_URL']: Config.HEALTH_JOURNEY_MULE_API_URL,
};
export const clearTokenStorage = async (): Promise<boolean> => {
  try {
    const key = 'accessToken'; // Same key used during saving
    await AsyncStorage.removeItem(key); // Remove the token
    return true; // Indicate success
  } catch (error) {
    return false; // Indicate failure
  }
};

export const setRefreshToken = async (
  refreshToken: string,
  expiresIn: number, // in seconds
): Promise<boolean> => {
  if (!refreshToken) return false;

  try {
    // Store token securely in Keychain
    await Keychain.setGenericPassword('password', refreshToken, {
      service: 'com.yourApp.refreshToken',
      accessible: Keychain.ACCESSIBLE.WHEN_UNLOCKED,
    });

    // Store expiry timestamp (in milliseconds)
    const expiryTime = Date.now() + expiresIn * 1000;
    await AsyncStorage.setItem('refreshTokenExpiry', expiryTime.toString());

    return true;
  } catch (error) {
    return false;
  }
};

//mule
export const setMuleRefreshToken = async (
  service: string,
  refreshToken: string,
) => {
  //const biometricSupport = await Keychain.getSupportedBiometryType();
  if (refreshToken) {
    try {
      // Save the refresh token under the given service name.
      await Keychain.setGenericPassword('password', refreshToken, {
        service: service, // Unique service name (e.g., 'com.yourApp.refreshMuleToken')
        accessible: Keychain.ACCESSIBLE.WHEN_UNLOCKED,
      });

      return true;
    } catch (error) {
      return false;
    }
  }
};
// const routeNames = useNavigationState(state => state?.routeNames);

export const setAccessToken = async (
  accessToken: string,
  expiresIn: number, // seconds
): Promise<boolean> => {
  try {
    const key = 'accessToken';
    const expiryKey = 'accessTokenExpiry';
    const expiryTime = Date.now() + expiresIn * 1000; // store in ms

    await AsyncStorage.setItem(key, accessToken);
    await AsyncStorage.setItem(expiryKey, expiryTime.toString());

    return true;
  } catch (error) {
    return false;
  }
};

export const setAccessTokenMule = async (
  accessTokenMule: string,
  expiresIn: number, // in seconds
): Promise<boolean> => {
  try {
    const key = 'accessTokenMule';
    const expiryKey = 'accessTokenMuleExpiry';
    const expiryTime = Date.now() + expiresIn * 1000; // expiration in ms

    await AsyncStorage.setItem(key, accessTokenMule);
    await AsyncStorage.setItem(expiryKey, expiryTime.toString());

    return true;
  } catch (error) {
    return false;
  }
};

export const setAutoFillPassword = async (
  password: string,
  username: string,
) => {
  try {
    await Keychain.setInternetCredentials(
      'autoFillPassword',
      username,
      password,
      {
        accessible: Keychain.ACCESSIBLE.WHEN_UNLOCKED,
      },
    );
    return true;
  } catch (error) {
    return false;
  }
};
export const getUserName = async () => {
  try {
    const credentials = await Keychain.getGenericPassword();
    if (credentials && credentials.username === 'username') {
      return credentials.password;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const setUserNameToken = async (userName: string) => {
  //const biometricSupport = await Keychain.getSupportedBiometryType();
  if (userName) {
    try {
      await Keychain.setGenericPassword('username', userName, {
        accessible: Keychain.ACCESSIBLE.WHEN_UNLOCKED,
      });
      return true;
    } catch (error) {
      return false;
    }
  }
};

export const getAccessToken = async (): Promise<string | null> => {
  try {
    const key = 'accessToken';
    const expiryKey = 'accessTokenExpiry';

    const [token, expiry] = await Promise.all([
      AsyncStorage.getItem(key),
      AsyncStorage.getItem(expiryKey),
    ]);

    if (!token || !expiry) return null;

    const now = Date.now();
    if (now > Number(expiry)) {
      // Token expired — clear it
      await AsyncStorage.multiRemove([key, expiryKey]);
      return null;
    }

    return token;
  } catch (error) {
    return null;
  }
};

export const getAccessTokenMule = async (): Promise<string | null> => {
  try {
    const key = 'accessTokenMule';
    const expiryKey = 'accessTokenMuleExpiry';

    const [token, expiry] = await Promise.all([
      AsyncStorage.getItem(key),
      AsyncStorage.getItem(expiryKey),
    ]);

    if (!token || !expiry) return null;

    const now = Date.now();
    if (now > Number(expiry)) {
      // Token expired — clean up
      await AsyncStorage.multiRemove([key, expiryKey]);
      return null;
    }

    return token;
  } catch (error) {
    return null;
  }
};

export const getAutoFillPassword = async (username: string) => {
  try {
    // Retrieve the access token using the same service and username
    const credentials = await Keychain.getInternetCredentials(
      'autoFillPassword',
    );
    if (credentials && credentials.username === username) {
      return credentials.password; // Return the token if username matches
    }

    return null; // Return null if no credentials found or username does not match
  } catch (error) {
    return null; // Return null in case of error
  }
};

interface FetchJSONResult {
  error: string | null;
  data: Record<string, any> | any[] | null;
  meta: {
    status: number;
    statusText?: string;
    clientError: boolean;
    serverError?: boolean;
    body: string | null;
  };
  responseHeaders?: any[];
}

export const fetchJSON = async (
  endPoint: any,
  opt: {
    method?: string;
    body?: Record<string, any>;
    isRawBody?: boolean;
    params?: Record<string, any>;
    URLParams?: string;
    isMuleApi?: boolean;
    isHealthyHabits?: boolean;
    isHealthProfile?: boolean;
    isToken?: boolean;
    muleHeaders?: Record<string, string>;
    apiBaseOverride?: string;
    queryParams?: Record<string, any>;
    treatPathAsAbsolute?: boolean;
    additionalHeaders?: Record<string, string>;
    overrideHeaders?: boolean;
  } = {},
  getHeaders: string[] = [],
  isMock = false,
): Promise<FetchJSONResult> => {
  const {
    body = {},
    method = REQUEST_GET,
    params = {},
    URLParams = '',
    isMuleApi = false,
    isHealthyHabits = false,
    isHealthProfile = false,
    isToken = false,
    muleHeaders = {},
    apiBaseOverride,
    queryParams = {},
    treatPathAsAbsolute = false,
    additionalHeaders = {},
    overrideHeaders = false,
    isRawBody = false,
  } = opt;

  const result: FetchJSONResult = {
    error: null,
    data: null,
    meta: {
      status: 0,
      clientError: false,
      body: null,
    },
  };

  let responseHeaders: any = [];
  let url = '';
  let requestSentTime = '';
  let responseReceiveTime = '';

  const defaultHeaders: Record<string, string> = isMuleApi
    ? {...muleHeaders}
    : {Accept: 'application/json'};

  if (!isMuleApi && !isRawBody) {
    defaultHeaders['Content-Type'] = 'application/json';
  }

  const headers: Record<string, string> = overrideHeaders
    ? {...additionalHeaders}
    : {...defaultHeaders, ...additionalHeaders};

  try {
    if (!overrideHeaders) {
      let token: string | null = null;

      if (isMuleApi) {
        token = await getAccessTokenMule();
      } else {
        token = await getAccessToken();
      }
      const isLoginAPI =
        endPoint.includes('login') ||
        endPoint.includes('reset-password') ||
        endPoint.includes('refresh_token') ||
        endPoint.includes('token');

      if (!token && !isLoginAPI) {
        result.error = 'Access token expired or not found.';
        result.meta.status = 401;
        result.meta.clientError = true;

        Logger.requestSplunkLog({
          logType: 'Error',
          message: `Blocked API call to "${endPoint}" due to expired or missing token`,
          severity: 'LOW',
          requestData: {
            requestQueryParameters: URLParams,
            requestHeaders: headers,
            requestBody: body,
            timestamp: new Date().toISOString(),
          },
          responseData: {
            responseBody: result.error,
            statusCode: 401,
            responseReceivedTime: new Date().toISOString(),
          },
        });
        // 🔁 Trigger logout + redirect logic from signOut
        await redirectToLogin();
        return result;
      }

      if ((token && isHealthyHabits) || isHealthProfile) {
        headers.authorization = `ca-token ${token}`;
      } else if (token && !isMuleApi) {
        headers.authorization = token;
      }
    }

    const defaultApi = isMuleApi
      ? API_TYPE.MULE_API_URL
      : isToken
      ? API_TYPE.HEALTH_JOURNEY_MULE_API_URL
      : isHealthyHabits
      ? API_TYPE.HEALTHY_HABITS_ROOT
      : isHealthProfile
      ? API_TYPE.HEALTH_PROFILE_ROOT
      : !isMock
      ? API_TYPE.CDP_API_URL
      : API_TYPE.MOCK_API_URL;

    const baseApi = treatPathAsAbsolute
      ? ''
      : apiBaseOverride && apiBaseOverride.length > 0
      ? apiBaseOverride
      : defaultApi;

    let URLParamsForRequest = '';
    if (!isEmpty(URLParams)) {
      URLParamsForRequest = `/${URLParams}`;
    }
    const hasQueryParams = queryParams && !isEmpty(queryParams);
    const serializedQuery = hasQueryParams
      ? stringify(queryParams, {skipEmptyString: true, skipNull: true})
      : '';
    const queryString = serializedQuery
      ? `${endPoint.includes('?') ? '&' : '?'}${serializedQuery}`
      : '';

    url = `${baseApi}${endPoint}${URLParamsForRequest}${queryString}`;

    const monitor = await getPerfMonitorDefaultInstance().getNetworkMetric({
      url,
      method,
    });

    requestSentTime = getDateISOStringWithoutTimezoneWithMill(new Date());
    await monitor.start();
    const axiosConfig: AxiosRequestConfig = {
      url,
      method: method as Method,
      headers,
      data: method !== REQUEST_GET ? body : undefined,
      params: method === REQUEST_GET ? body : params,
      transformRequest: isRawBody ? [(data: any) => data] : undefined,
    };

    const response: AxiosResponse = await axios(axiosConfig);
    responseReceiveTime = getDateISOStringWithoutTimezoneWithMill(new Date());

    // Normalize all axios response headers to lowercase
    const normalizedHeaders: Record<string, string> = {};
    Object.entries(response.headers).forEach(([key, value]) => {
      normalizedHeaders[key.toLowerCase()] = value;
    });

    getHeaders.forEach(header => {
      responseHeaders.push(response.headers[header.toLowerCase()] ?? null);
    });

    const statusCode = response.status;
    result.meta.status = statusCode;
    result.meta.statusText = response.statusText;
    result.meta.clientError = statusCode >= 400 && statusCode < 500;
    result.meta.serverError = response.status >= 500;

    monitor.setResponseStatus(statusCode);
    await monitor.stop();

    result.meta.body = JSON.stringify(response.data);
    const transformData = flow(snakeToCamelCase);
    result.data = transformData(response.data);

    if (result.meta.clientError || result.meta.serverError) {
      result.error = get(result.data, 'message', new ApiError().name);
    }

    Logger.requestSplunkLog({
      logType: 'request',
      message: `Successfully received response for "${url}"`,
      severity: 'LOW',
      requestData: {
        requestQueryParameters: URLParams,
        requestHeaders: headers,
        requestBody: body,
        timestamp: requestSentTime,
      },
      responseData: {
        responseHeaders: response.headers,
        responseBody: result.data,
        statusCode: result.meta.status,
        responseReceiveTime,
      },
    });
  } catch (ex: any) {
    responseReceiveTime = getDateISOStringWithoutTimezoneWithMill(new Date());
    result.meta.clientError = true;

    const errorMessage = ex?.message || 'Request failed';
    if (axios.isAxiosError(ex)) {
      if (!ex.response) {
        result.meta.status = 500;
        result.error = errorMessage;
        result.meta.serverError = true;
      } else {
        const statusCode = ex.response.status ?? 500;
        result.meta.status = statusCode;
        result.meta.serverError = statusCode >= 500;
        result.meta.body = JSON.stringify(ex.response.data ?? '') || null;
        result.error = (ex.response.data as any)?.message || errorMessage;
      }
    } else if (ex instanceof Error) {
      result.meta.status = 500;
      result.error = ex.message;
      result.meta.serverError = true;
    } else {
      result.meta.status = 500;
      result.error = 'Request failed';
      result.meta.serverError = true;
    }

    // Try to parse the body if not already
    if (!isJSON(result.meta.body)) {
      result.meta.serverError = true;
      result.error = `${errorMessage}. '${result.meta.body}'`;
    } else {
      try {
        result.data = JSON.parse(result.meta.body!);
      } catch {}
    }

    Logger.requestSplunkLog({
      logType: 'Error',
      message: `Error response for "${url}"`,
      severity: 'HIGH',
      requestData: {
        requestQueryParameters: URLParams,
        requestHeaders: headers,
        requestBody: body,
        timestamp: requestSentTime,
      },
      responseData: {
        responseBody: result.error,
        statusCode: result.meta.status,
        responseReceiveTime,
      },
    });
  }
  return isEmpty(responseHeaders) ? result : {...result, responseHeaders};
};
